import { useEffect, useState } from 'react'

// Extend the Event interface for the beforeinstallprompt event
interface BeforeInstallPromptEvent extends Event {
  readonly platforms: Array<string>
  readonly userChoice: Promise<{
    outcome: 'accepted' | 'dismissed'
    platform: string
  }>
  prompt(): Promise<void>
}

export const PWA_STORAGE_KEYS = {
  INSTALLED: 'pwa_installed',
  DISMISSED: 'pwa_prompt_dismissed',
} as const

export function usePWAInstall() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null)
  const [isInstallable, setIsInstallable] = useState(false)
  const [isInstalled, setIsInstalled] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false
    return (
      localStorage.getItem(PWA_STORAGE_KEYS.INSTALLED) === 'true' ||
      window.matchMedia('(display-mode: standalone)').matches ||
      ('standalone' in window.navigator && (window.navigator as any).standalone === true)
    )
  })
  const [isIOS, setIsIOS] = useState(false)

  useEffect(() => {
    // Check if it's an iOS device
    const userAgent = window.navigator.userAgent.toLowerCase()
    const isIosDevice = /iphone|ipad|ipod/.test(userAgent)
    const isMacSafari =
      /macintosh/.test(userAgent) && /safari/.test(userAgent) && !/chrome|chromium|edg|crios|fxios/.test(userAgent)
    setIsIOS(isIosDevice || isMacSafari)

    // Check if already installed (standalone mode)
    if (
      localStorage.getItem(PWA_STORAGE_KEYS.INSTALLED) === 'true' ||
      window.matchMedia('(display-mode: standalone)').matches ||
      ('standalone' in window.navigator && (window.navigator as any).standalone === true)
    ) {
      setIsInstalled(true)
      localStorage.setItem(PWA_STORAGE_KEYS.INSTALLED, 'true')
    }

    // Handle beforeinstallprompt
    const handleBeforeInstallPrompt = (e: Event) => {
      // Prevent the mini-infobar from appearing on mobile
      e.preventDefault()
      // Stash the event so it can be triggered later.
      setDeferredPrompt(e as BeforeInstallPromptEvent)
      setIsInstallable(true)
    }

    // Handle successful installation
    const handleAppInstalled = () => {
      setIsInstalled(true)
      setIsInstallable(false)
      setDeferredPrompt(null)
      localStorage.setItem(PWA_STORAGE_KEYS.INSTALLED, 'true')
      localStorage.removeItem(PWA_STORAGE_KEYS.DISMISSED)
    }

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
    window.addEventListener('appinstalled', handleAppInstalled)

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
      window.removeEventListener('appinstalled', handleAppInstalled)
    }
  }, [])

  const promptInstall = async (): Promise<boolean> => {
    if (deferredPrompt) {
      try {
        // Show the install prompt
        await deferredPrompt.prompt()
        // Wait for the user to respond to the prompt
        const { outcome } = await deferredPrompt.userChoice

        if (outcome === 'accepted') {
          setIsInstallable(false)
          setIsInstalled(true)
          localStorage.setItem(PWA_STORAGE_KEYS.INSTALLED, 'true')
          localStorage.removeItem(PWA_STORAGE_KEYS.DISMISSED)
        } else if (outcome === 'dismissed') {
          localStorage.setItem(PWA_STORAGE_KEYS.DISMISSED, 'true')
        }

        // We've used the prompt, and can't use it again, throw it away
        setDeferredPrompt(null)
        return true
      } catch (e) {
        console.error('PWA prompt install error:', e)
        setDeferredPrompt(null)
        return false
      }
    }
    return false
  }

  return { isInstallable, isInstalled, isIOS, promptInstall }
}
